/**
 * WHEN A FAILED REQUEST MAY SHOW ITS TOAST.
 *
 * Every API error used to raise its own toast from the axios interceptor. A
 * page whose reads all fail at once — the server down, a stale username that
 * no longer resolves — sent a burst of identical «Operation failed» toasts, and
 * React Query's retries sent each one again, several a second.
 *
 * - One toast per message: the same words share one toast id, so a repeat
 *   replaces the toast on screen instead of stacking a second one.
 * - A failed READ (GET) shows its message once and then keeps quiet about it
 *   for a while: the page has its own failed state with a retry, and the
 *   retries of the same read are not news.
 * - A failed ACTION (anything else) always speaks — the reader just pressed
 *   something and is owed an answer — but through the same shared id.
 * - No response at all (the server unreachable) is one plain message, held
 *   back the same way whatever the request was.
 */

export const READ_QUIET_MS = 30_000;

export type ToastPlan = { show: false } | { show: true; id: string; message: string };

export function planErrorToast(
  input: { method?: string; hasResponse: boolean; message: string; networkMessage: string },
  seen: Map<string, number>,
  now: number,
): ToastPlan {
  const network = !input.hasResponse;
  const message = network ? input.networkMessage : input.message;
  const id = network ? "api-error:network" : `api-error:${message}`;
  const isRead = String(input.method ?? "get").toLowerCase() === "get";
  if (network || isRead) {
    const last = seen.get(id);
    if (last !== undefined && now - last < READ_QUIET_MS) return { show: false };
  }
  seen.set(id, now);
  return { show: true, id, message };
}
